import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/AuthContext';

// Rendered when AuthContext's recoveryMode is true. The user landed here via a
// "reset your password" email link; supabase-js has already established the
// session from the URL fragment, so we just need to capture the new password
// and call updateUser.
export default function SetNewPassword() {
  const { updatePassword, signOut, clearRecoveryMode } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)    { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    setError(null);
    const { error: msg } = await updatePassword(pw);
    if (msg) {
      setError(msg);
      setSubmitting(false);
      return;
    }
    clearRecoveryMode();
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">Set a new password</h1>
          <p className="text-sm text-slate-400">You're signed in via the email link. Pick a new password.</p>
        </div>

        <label className="block">
          <span className="text-xs text-slate-400">New password</span>
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus required className="input mt-1" />
        </label>
        <label className="block">
          <span className="text-xs text-slate-400">Confirm</span>
          <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required className="input mt-1" />
        </label>

        {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</div>}

        <button
          type="submit"
          disabled={submitting || !pw || !pw2}
          className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-800 disabled:text-slate-500"
        >
          {submitting ? 'Updating…' : 'Set password'}
        </button>
        <button
          type="button"
          onClick={async () => { await signOut(); }}
          className="w-full text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel and sign out
        </button>
      </form>
    </div>
  );
}
